import React, { useState, useContext, useEffect } from "react";
import Range from "../../leftSide/Files/Range";
import units from "../../../utils/units";
import {
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
} from "@mui/material";
import addIcon from "../../../res/icons/add.png";
import deleteIcon from "../../../res/icons/delete.png";
import "./parameters.scss";
import { deepCopy } from "../../leftSide/Models/modelLogic";
import {
  convertToKelvin,
  doesAnyRangeWrapZero,
  filterCurveDataToRange,
  generateModelsRangesQuantPairsForModelDist,
  getRangesForModelsFromGraphs,
  segmentsIntoSingleCurve,
  splitCurveIntoSegmentsByEdges,
  isInverseUnit,
  cm1ToAnyUnit,
  anyUnitToCm1,
} from "../../../utils/helpers";
// import { processFileWithOptions } from "../../leftSide/Files/fileProcessing/processingLogic";
import { AuthContext } from "../../../context/AuthContext";
import { GraphContext } from "../../../context/GraphContext";
import { DashboardContext } from "../../../context/DashboardContext";
import { GeneralContext } from "../../../context/GeneralContext";
import { createModelDistPayload } from "../../middle/graphLogic";

function FileSettings(props) {
  const { file, confirmFile, modelID, modelSpeqqleID } = props;
  const { currentUser } = useContext(AuthContext);
  const { graphs } = useContext(GraphContext);
  const { modelData, defaultXAxisUnit } = useContext(DashboardContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const [rangeMin, setRangeMin] = useState(file.dataRangeMin);
  const [rangeMax, setRangeMax] = useState(file.dataRangeMax);
  // Ensure edges have unit property (default to defaultXAxisUnit if missing)
  const edgesWithUnits = (file.edges || []).map((edge) => ({
    ...edge,
    unit: edge.unit || defaultXAxisUnit || units[0],
  })).filter(edge => edge.min !== "" && edge.max !== "");
  const [edges, setEdges] = useState(edgesWithUnits);
  const [pointsInRange, setPointsInRange] = useState("N/A");
  const [autoCut, setAutoCut] = useState(true);
  const [onlyPos, setOnlyPos] = useState(false);
  const [temperature, setTemperature] = useState("");
  const [tempUnits, setTempUnits] = useState("K");
  const [highlightReq, setHighlightReq] = useState(false);

  // Default to defaultXAxisUnit if it's not an inverse unit, otherwise fall back to units[0]
  const getDefaultDisplayRangeUnit = () => {
    try {
    if (defaultXAxisUnit && !isInverseUnit(defaultXAxisUnit)) {
      return defaultXAxisUnit;
    }
    return units[0];
    } catch (error) {
      recordedErrorLog("Default display range unit calculation failure: ", error);
      return units[0];
    }
  };
  const [displayRangeUnit, setDisplayRangeUnit] = useState(
    getDefaultDisplayRangeUnit()
  );
  const [displayRangeMin, setDisplayRangeMin] = useState(cm1ToAnyUnit(displayRangeUnit, file.dataRangeMin));
  const [displayRangeMax, setDisplayRangeMax] = useState(cm1ToAnyUnit(displayRangeUnit, file.dataRangeMax));

  // Filter units to only non-inverse units for range display
  const availableRangeUnits = units.filter((unit) => !isInverseUnit(unit));

  // Convert display values back to cm^-1 when updating
  const handleRangeMinChange = (value) => {
    try {
    if (value === "" || value === null || value === undefined) {
      setDisplayRangeMin("");
      setRangeMin("");
      return;
    }
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) {
      setRangeMin("");
      setDisplayRangeMin("");
      return;
    }
    // Convert from display unit back to cm^-1
    if (displayRangeUnit.value === units[0].value) {
      setRangeMin(numValue);
      setDisplayRangeMin(numValue);
    } else {
      const cm1Value = anyUnitToCm1(displayRangeUnit, numValue);
      setRangeMin(cm1Value);
      setDisplayRangeMin(numValue);
    }
    } catch (error) {
      recordedErrorLog("Range min change failure: ", error);
    }
  };

  const handleRangeMaxChange = (value) => {
    try {
    if (value === "" || value === null || value === undefined) {
      setRangeMax("");
      setDisplayRangeMax("");
      return;
    }
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) {
      setRangeMax("");
      setDisplayRangeMax("");
      return;
    }
    // Convert from display unit back to cm^-1
    if (displayRangeUnit.value === units[0].value) {
      setRangeMax(numValue);
      setDisplayRangeMax(numValue);
    } else {
      const cm1Value = anyUnitToCm1(displayRangeUnit, numValue);
      setRangeMax(cm1Value);
      setDisplayRangeMax(numValue);
    }
    } catch (error) {
      recordedErrorLog("Range max change failure: ", error);
    }
  };

  const handleDisplayRangeUnitChange = (e) => {
    try {
    const value = e.target.value;
    const selectedUnit = availableRangeUnits.find((u) => u.value === value);
    if (selectedUnit) {
      setDisplayRangeUnit(selectedUnit);
      if (rangeMin !== "") {
        setDisplayRangeMin(cm1ToAnyUnit(selectedUnit, rangeMin));
      } else {
        setDisplayRangeMin("");
      }
      if (rangeMax !== "") {
        setDisplayRangeMax(cm1ToAnyUnit(selectedUnit, rangeMax));
      } else {
        setDisplayRangeMax("");
      }
    }
    } catch (error) {
      recordedErrorLog("Display range unit change failure: ", error);
    }
  };

  const handleChange = (event) => {
    setTempUnits(event.target.value);
  };

  function updateCutMin(value, index) {
    try {
      const cutParamsCopy = deepCopy(edges);
      if (value === "") {
        cutParamsCopy[index] = { ...cutParamsCopy[index], min: value };
      } else {
        cutParamsCopy[index] = {
          ...cutParamsCopy[index],
          min: parseFloat(value),
        };
      }
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Min cut updated failure: ", error);
    }
  }

  function updateCutMax(value, index) {
    try {
      const cutParamsCopy = deepCopy(edges);
      if (value === "") {
        cutParamsCopy[index] = { ...cutParamsCopy[index], max: value };
      } else {
        cutParamsCopy[index] = {
          ...cutParamsCopy[index],
          max: parseFloat(value),
        };
      }
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Max cut update failure: ", error);
    }
  }

  function handleAddCut() {
    setEdges([...edges, { min: "", max: "", unit: defaultXAxisUnit || units[0] }]);
  }

  function handleDeleteCut(index) {
    setEdges(edges.filter((_, i) => i !== index));
  }

  /**
   * This function updates the unit for an edge without converting min/max values.
   * @function updateEdgeUnit
   * @param {Event} e - The event from the unit selector
   * @param {number} index - The index of the edge to update
   */
  function updateEdgeUnit(e, index) {
    try {
      const value = e.target.value;
      const newUnit = units.find((u) => u.value === value);
      if (!newUnit) return;

      const cutParamsCopy = deepCopy(edges);
      const currentEdge = cutParamsCopy[index];

      // Only update the unit, don't convert min/max values
      cutParamsCopy[index] = {
        ...currentEdge,
        unit: newUnit,
      };
      setEdges(cutParamsCopy);
    } catch (error) {
      recordedErrorLog("Edge unit update failure: ", error);
    }
  }

  // Calculate pointsInRange when file, range, or edges change
  useEffect(() => {
    try {
      let cutData = filterCurveDataToRange(file.dataPoints, rangeMin, rangeMax);

      cutData = segmentsIntoSingleCurve(
        splitCurveIntoSegmentsByEdges(cutData, edges)
      );

      setPointsInRange(cutData.length);
    } catch (error) {
      recordedErrorLog("Points in range calculation failure: ", error);
      setPointsInRange("N/A");
    }
  }, [file, rangeMin, rangeMax, edges]);

  const handleConfirmClick = () => {
    try {
      if (onlyPos && (temperature == "" || temperature == undefined)) {
        setHighlightReq(true);
      } else {
        let data = file.dataPoints;

        data = filterCurveDataToRange(
          data,
          file.dataRangeMin,
          file.dataRangeMax
        );

        data = segmentsIntoSingleCurve(
          splitCurveIntoSegmentsByEdges(data, file.edges)
        );

        const filteredCutParams = edges.filter(
          (param) =>
            (param.min !== undefined && param.min !== "") ||
            (param.max !== undefined && param.max !== "")
        );

        let fitinfoLoc = {
          peak0auto: autoCut,
          edges: filteredCutParams,
          onlypos: onlyPos,
          npoints: file.npoints,
        };
        if (temperature != "") {
          fitinfoLoc = {
            ...fitinfoLoc,
            temperature: convertToKelvin(parseFloat(temperature), tempUnits),
          };
        }

        let range = {};
        range = { ...range, min: rangeMin != "" ? rangeMin : -1000 };
        range = { ...range, max: rangeMax != "" ? rangeMax : 1000 };

        if (Object.keys(range).length > 0) {
          fitinfoLoc = { ...fitinfoLoc, range: range };
        }

        const rangesForModels = getRangesForModelsFromGraphs(graphs);

        const parametersForCurvePayload =
          generateModelsRangesQuantPairsForModelDist(
            [modelID],
            rangesForModels,
            modelData
          );

        let payload = {
          User: currentUser.id,
          Data: {
            SendData: [
              {
                dataid: file.ID,
                data: data,
              },
            ],
          },
          Fit: {
            AutoFit: [
              {
                modeltype: modelSpeqqleID,
                modelid: modelID,
                dataid: file.ID,
                fitinfo: fitinfoLoc,
                quantity: 5, // Default Raman, we keep it 5 until there are more models with AutoFit functionality
              },
            ],
          },
        };

        if (parametersForCurvePayload.models.length > 0) {
          const modelDistPayload = createModelDistPayload(
            parametersForCurvePayload.models,
            rangesForModels,
            parametersForCurvePayload.quantityPairs,
            currentUser
          );
          payload = {
            ...payload,
            Model: modelDistPayload.Model,
          };
        }

        confirmFile(payload, modelID);
      }
    } catch (error) {
      recordedErrorLog("Confirm click handler failure: ", error);
    }
  };

  return (
    <div className="fileSettingContainer" data-testid="file-settings">
      <div className="fileName" id="autofit-data-file-name">
        <div>Data to fit:</div>
        <div>{file.name}</div>
      </div>
      <div>
        <div
          className="rangeTitle"
          title="Set the outer limits of the data range in which you want to fit and display your data."
        >
          Data Range:
        </div>
        <div className="rangeWithUnitSelector">
          <Range
            initialMin={displayRangeMin}
            initialMax={displayRangeMax}
            updateMin={handleRangeMinChange}
            updateMax={handleRangeMaxChange}
            rangeTitle={null}
          />
          <FormControl
            variant="standard"
            size="small"
            style={{ minWidth: "80px" }}
          >
            <Select
              MenuProps={{
                container: () => document.getElementById("autoFit-modal"),
                PaperProps: {
                  style: {
                    zIndex: 10000,
                  },
                },
              }}
              value={displayRangeUnit.value}
              onChange={handleDisplayRangeUnitChange}
              className="display_range_unit_select"
              renderValue={(value) => {
                const selectedUnit = availableRangeUnits.find(
                  (u) => u.value === value
                );
                if (!selectedUnit) return value;
                return selectedUnit.name;
              }}
            >
              {availableRangeUnits.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="autoCutting">
        <FormControlLabel
          control={
            <Checkbox onClick={() => setAutoCut(!autoCut)} checked={autoCut} />
          }
          label="Automatic 0-peak Cutting"
        />
        {!autoCut ? (
          <div className="note">Please exclude 0-peak manually</div>
        ) : (
          <></>
        )}
      </div>
      <div className="onlyPos">
        <FormControlLabel
          control={
            <Checkbox onClick={() => setOnlyPos(!onlyPos)} checked={onlyPos} />
          }
          label="Positive Values only"
        />
      </div>
      <div className="temperature">
        <TextField
          required={onlyPos}
          style={{
            height: "100%",
            width: "80px",
          }}
          label="Temp"
          value={temperature}
          onChange={(e) => setTemperature(e.target.value)}
          className="rangeVal"
          size="small"
          type="number"
          error={highlightReq}
        />
        <Select
          labelId="demo-simple-select-label"
          MenuProps={{
            container: () => document.getElementById("autoFit-modal"),
          }}
          id="demo-simple-select"
          value={tempUnits}
          defaultValue="K"
          onChange={handleChange}
          size="small"
          className="tempDropdown"
        >
          <MenuItem value={"C"}>C</MenuItem>
          <MenuItem value={"K"}>K</MenuItem>
          <MenuItem value={"F"}>F</MenuItem>
        </Select>
      </div>
      <div className="cutSettingsTitle">Exclusion Ranges:</div>
      <div
        className="allCutsContainer"
        style={edges.length > 1 ? {} : { height: "75px" }}
      >
        {edges && edges.length > 0 ? (
          edges.map((param, index) => {
            const edgeUnit = param.unit || defaultXAxisUnit || units[0];
            return (
              <div className="cutContainer" key={index}>
                <div className="rangeWithUnitSelector">
                  <Range
                    initialMin={param.min}
                    initialMax={param.max}
                    updateMin={updateCutMin}
                    updateMax={updateCutMax}
                    rangeTitle={null}
                    index={index}
                    required={index === 0 && !autoCut}
                  />
                  <FormControl
                    variant="standard"
                    size="small"
                    style={{ minWidth: "80px" }}
                  >
                    <Select
                      MenuProps={{
                        container: () =>
                          document.getElementById("autoFit-modal"),
                        PaperProps: {
                          style: {
                            zIndex: 10000,
                          },
                        },
                      }}
                      value={edgeUnit.value}
                      onChange={(e) => updateEdgeUnit(e, index)}
                      className="edge_unit_select"
                      renderValue={(value) => {
                        const selectedUnit = units.find(
                          (u) => u.value === value
                        );
                        if (!selectedUnit) return value;
                        return selectedUnit.name;
                      }}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <img
                  src={deleteIcon}
                  alt="delete Cut"
                  className="deleteCut"
                  onClick={() => handleDeleteCut(index)}
                />
              </div>
            );
          })
        ) : (
          <div style={{ color: "grey", fontStyle: "italic", paddingLeft: "5px" }}>
            None
          </div>
        )}
        <img
          src={addIcon}
          alt="add another Cut"
          className="addCut"
          onClick={() => handleAddCut()}
        />
      </div>
      <div className="confirmButtonSection">
        <div className="warningNote">
          The Autofit will reset all model parameters
        </div>
        <Button
          variant="contained"
          size="small"
          sx={{ m: 1 }}
          onClick={() => handleConfirmClick()}
          className="confirmButton"
          id="confirm-autofit-button"
          disabled={
            (!autoCut && edges.length > 0 && !doesAnyRangeWrapZero(edges)) ||
            pointsInRange < 50 ||
            pointsInRange === "N/A" ||
            rangeMin === "" ||
            rangeMax === "" ||
            edges.some(edge => edge.min === "" || edge.max === "")
          }
        >
          {"Autofit on " + pointsInRange + " data points"}
        </Button>
      </div>
    </div>
  );
}

export default FileSettings;
